import { Modal } from "@shared/ui";
import { getGoogleSheetUrl } from "../../lib/constants";
import styles from "../BulkAddModal/styles.module.scss";
import { ProcessFooter } from "../BulkAddModal/ui/ProcessFooter";
import { UploadFooter, UploadStep } from "../BulkAddModal/ui/UploadStep";
import { ProcessStep } from "./ui/ProcessStep";
import { useBulkEditModal } from "./useBulkEditModal";

interface BulkEditBriefInfluencersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function BulkEditBriefInfluencersModal({
  isOpen,
  onClose,
  onSuccess,
}: BulkEditBriefInfluencersModalProps) {
  const {
    briefId,
    step,
    setStep,
    reviewTab,
    setReviewTab,
    parsedRows,
    isSyncing,
    isDownloadingXlsx,
    isParsingEditCreatorsCsv,
    isBulkActions,
    deliverables,
    hasNoDeliverables,
    configurators,
    customFields,
    templateSheetId,
    processedRows,
    downloadBriefXlsx,
    parseEditCreatorsCsv,

    handleImport,
  } = useBulkEditModal({ isOpen, onClose, onSuccess });

  const isProcessMode = step === "process";

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={onClose}
        title={isProcessMode ? "Review updates" : "Bulk Edit Creators"}
        footer={null}
        width={isProcessMode ? 920 : 600}
        className={styles.modal}
        destroyOnClose
        styles={{
          body: { padding: 0 },
          header: { marginBottom: 0, padding: 20 },
          content: { padding: 0 },
        }}
      >
        <div className={styles.modalContent}>
          {isProcessMode ? (
            <>
              <ProcessStep
                rows={parsedRows}
                deliverables={deliverables ?? []}
                configurators={configurators}
                customFields={customFields}
                activeTab={reviewTab}
                onTabChange={setReviewTab}
              />
              <ProcessFooter
                processedCount={processedRows.length}
                skippedCount={parsedRows.length - processedRows.length}
                reviewTab={reviewTab}
                isImporting={isBulkActions}
                confirmLabel={`Update ${processedRows.length} creator${
                  processedRows.length === 1 ? "" : "s"
                }`}
                onBack={() => {
                  setReviewTab("processed");
                  setStep("upload");
                }}
                onViewSkipped={() => setReviewTab("error")}
                onImport={handleImport}
              />
            </>
          ) : (
            <>
              <div className={styles.modalBody}>
                <UploadStep
                  briefId={briefId}
                  templateSheetId={templateSheetId}
                  isSyncing={isSyncing}
                  isParsing={isParsingEditCreatorsCsv}
                  isDownloadingXlsx={isDownloadingXlsx}
                  hasNoDeliverables={false}
                  storageFolder={`campaign-briefs/${briefId}/bulk-edit-creators`}
                  description="Upload a CSV to update existing creators’ deliverables and custom fields. After processing you’ll review ready rows and any failed updates."

                  onDownloadXlsx={() => {
                    if (!briefId) return;
                    downloadBriefXlsx({ briefId, type: "editCreators" });
                  }}
                  onDownloadTemplate={() => {
                    if (!templateSheetId) return;
                    window.open(getGoogleSheetUrl(templateSheetId), "_blank");
                  }}
                  onCsvUploaded={(csvUrl) => {
                    parseEditCreatorsCsv({ briefId, csv: csvUrl });
                  }}
                />
              </div>
              <UploadFooter onClose={onClose} />
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
